// Cross-cutting prompter for the two agreement gates (#3287).
//
// Background: the backend refuses a handful of POST routes until the org has
// accepted an agreement — 403 { error: "dpa_required" } for the
// databehandleravtale (#1887) and 403 { error: "terms_required" } for the
// operatøravtale (#3025 P1). apiClient.ts turns either into a bounce to the
// accept screen.
//
// That bounce used to fire the instant the 403 landed: the operator clicked
// "Oppgrader til Team", watched a spinner, and the whole SPA replaced itself
// with a legal screen — no modal, no toast, no way back. It reads as coercive
// rather than as one more step, and the operator loses both their place and
// the context of what they were trying to do. So the bounce now asks first.
//
// Why a global prompter and not per-component handling? Same reasoning as
// passkeySudoPrompt.ts, which this mirrors: the 403 can come from any gated
// route (billing checkout, payments connect, booking / meeting / customer
// creation, bulk import) and none of those surfaces should reimplement the
// modal choreography. apiClient.ts asks; the app shell mounts a modal and
// registers it as the prompter on mount.
//
// Contract: the prompter resolves `true` when the operator chose to continue
// to the accept screen, `false` when they backed out. On false the caller
// leaves them exactly where they were — the action simply didn't happen, and
// the backend keeps refusing it until they accept.

export type AgreementKind = 'dpa' | 'terms'

export type AgreementGatePrompter = (kind: AgreementKind) => Promise<boolean>

// Default: no prompter wired — proceed with the bounce. This is the opposite
// default from passkeySudoPrompt (which denies), and deliberately so: there
// the fallback is "surface the error inline", here it is "the gate is
// blocking and the only way through is the accept screen". Defaulting to
// false would strand a pre-mount or test-context operator on a surface whose
// every attempt silently fails.
const proceed: AgreementGatePrompter = async () => true

let prompter: AgreementGatePrompter = proceed

export function setAgreementGatePrompter(p: AgreementGatePrompter): void {
  prompter = p
}

export function clearAgreementGatePrompter(): void {
  prompter = proceed
}

// Single-flight coalescing per kind — a dashboard that fires several gated
// mutations at once must open ONE modal, not a stack of them. Keyed by kind
// because the two agreements version independently: an org can owe both, and
// the operator should be asked about each on its own terms rather than having
// one prompt silently answer for the other.
const inflight = new Map<AgreementKind, Promise<boolean>>()

export function requestAgreementConsent(kind: AgreementKind): Promise<boolean> {
  const existing = inflight.get(kind)
  if (existing) return existing
  const p = prompter(kind).finally(() => {
    inflight.delete(kind)
  })
  inflight.set(kind, p)
  return p
}

// Marker put on the rejection when the operator backed OUT of the prompt.
//
// The gated call still has to reject — every call site resets its loading flag
// in a catch/finally, and a promise that never settles leaves the button
// spinning forever. But "you chose not to continue" is not a failure, and the
// generic 403 message ("Ingen tilgang") or a caller's own fallback ("Kunne
// ikke starte betalingen") would tell the operator something broke when
// nothing did. errorMessage() renders these silently; see apiError.ts.
export const AGREEMENT_DECLINED_CODE = 'agreement_declined'

export function isAgreementDeclined(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const body = (e as { body?: unknown }).body
  if (!body || typeof body !== 'object') return false
  return (body as { code?: unknown }).code === AGREEMENT_DECLINED_CODE
}
